import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { alpha, Box, useTheme, type SxProps, type Theme } from "@mui/material";
import { designTokens } from "@/theme";

export type ScrollFadeContainerProps = {
  children: ReactNode;
  /** Applied to the scrollable element. */
  scrollSx?: SxProps<Theme>;
  /** Accessible label for the scrollable region. */
  "aria-label"?: string;
  /** Optional ref to the scrollable element (e.g. collapsible header sync). */
  scrollRef?: RefObject<HTMLDivElement | null>;
};

const SCROLL_EDGE_THRESHOLD_PX = 4;
const FADE_HEIGHT_PX = designTokens.spacing.xl;

/**
 * Scrollable region with top/bottom fade cues when content overflows.
 */
export const ScrollFadeContainer = memo(function ScrollFadeContainer({
  children,
  scrollSx,
  "aria-label": ariaLabel,
  scrollRef: scrollRefProp,
}: ScrollFadeContainerProps) {
  const theme = useTheme();
  const localScrollRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollRefProp ?? localScrollRef;
  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);

  const updateFadeState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const canScroll = scrollHeight > clientHeight + SCROLL_EDGE_THRESHOLD_PX;
    setFadeTop(canScroll && scrollTop > SCROLL_EDGE_THRESHOLD_PX);
    setFadeBottom(
      canScroll && scrollTop + clientHeight < scrollHeight - SCROLL_EDGE_THRESHOLD_PX
    );
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateFadeState();
    el.addEventListener("scroll", updateFadeState, { passive: true });

    if (typeof ResizeObserver === "undefined") {
      return () => {
        el.removeEventListener("scroll", updateFadeState);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      updateFadeState();
    });
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateFadeState);
      resizeObserver.disconnect();
    };
  }, [updateFadeState, children, scrollRef]);

  const fadeColor = theme.palette.background.paper;

  return (
    <Box
      sx={{
        position: "relative",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {fadeTop ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: FADE_HEIGHT_PX,
            pointerEvents: "none",
            zIndex: 1,
            background: `linear-gradient(to bottom, ${fadeColor}, ${alpha(fadeColor, 0)})`,
          }}
        />
      ) : null}

      <Box
        ref={scrollRef}
        role="region"
        aria-label={ariaLabel}
        tabIndex={-1}
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          scrollbarGutter: "stable",
          scrollbarWidth: "thin",
          scrollbarColor: `${alpha(theme.palette.text.primary, 0.28)} transparent`,
          "&::-webkit-scrollbar": {
            width: 6,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.primary, 0.22),
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          ...scrollSx,
        }}
      >
        {children}
      </Box>

      {fadeBottom ? (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: FADE_HEIGHT_PX,
            pointerEvents: "none",
            zIndex: 1,
            background: `linear-gradient(to top, ${fadeColor}, ${alpha(fadeColor, 0)})`,
          }}
        />
      ) : null}
    </Box>
  );
});
